import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Lock, ExternalLink, Loader2, CalendarDays, X } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODES = [['journee', 'Journée'], ['matin', 'Matin'], ['apres_midi', 'Après-midi']];
const TYPE_LABEL = { formation: 'Formation', stage: 'Stage / PAE', prestation_externe: 'Externe' };

// Planning pedagogique d'une AF / AFT, editable en place (periode, lieu, formateur) — reutilisable depuis l'activite collaborateur, le Gantt...
export const PlanningAfDialog = ({ dossierId, open, onOpenChange, highlightDate, onSaved }) => {
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [planning, setPlanning] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [lieux, setLieux] = useState([]);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    if (!dossierId) return;
    setLoading(true);
    try {
      const [d, f, l, m] = await Promise.all([
        axios.get(`${API_URL}/dossiers/${dossierId}`), axios.get(`${API_URL}/formateurs`), axios.get(`${API_URL}/lieux`), axios.get(`${API_URL}/modules`),
      ]);
      setDossier(d.data);
      setPlanning([...(d.data.planning || [])].sort((a, b) => a.date.localeCompare(b.date)));
      setFormateurs((f.data || []).filter(x => x.is_active !== false).sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr')));
      setLieux(l.data || []);
      setModules(Object.fromEntries((m.data || []).map(x => [x.id, x])));
      setDirty(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Impossible de charger le planning'); } finally { setLoading(false); }
  }, [dossierId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const update = (date, demi, patch) => { setPlanning(prev => prev.map(p => (p.date === date && p.demi_journee === demi ? { ...p, ...patch } : p))); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/dossiers/${dossierId}/planning`, { planning });
      toast.success('Planning enregistré');
      setDirty(false);
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement'); } finally { setSaving(false); }
  };
  const close = () => { if (dirty) { save(); } else { onSaved?.(); onOpenChange(false); } };

  const lieuxOptions = useMemo(() => {
    const noms = new Set(lieux.map(l => l.nom));
    if (dossier?.lieu_principal) noms.add(dossier.lieu_principal);
    if (dossier?.lieu_secondaire) noms.add(dossier.lieu_secondaire);
    planning.forEach(p => { if (p.lieu) noms.add(p.lieu); });
    return [...noms].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [lieux, dossier, planning]);
  const nomFormateur = (id) => { const f = formateurs.find(x => x.id === id); return f ? `${f.nom} ${f.prenom}` : id ? 'Collaborateur inactif' : ''; };
  const horaires = dossier?.horaires || {};
  const horairesLabel = (demi) => demi === 'matin' ? `${horaires.matin_debut || '08:00'}–${horaires.matin_fin || '12:00'}` : demi === 'apres_midi' ? `${horaires.am_debut || '13:30'}–${horaires.am_fin || '16:30'}` : `${horaires.matin_debut || '08:00'}–${horaires.am_fin || '16:30'}`;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="planning-af-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <CalendarDays className="w-5 h-5 text-indigo-600" />
            <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${dossier?.type_dossier === 'aft' ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'}`}>{dossier?.code || '…'}</span>
            <span className="truncate">{dossier?.intitule || 'Planning pédagogique'}</span>
            {dossier && <button type="button" onClick={() => navigate(`/dossiers/${dossier.id}`)} className="ml-auto inline-flex items-center gap-1 text-xs font-normal text-indigo-600 hover:underline" data-testid="planning-af-open-fiche"><ExternalLink className="w-3.5 h-3.5" /> Fiche complète</button>}
          </DialogTitle>
          <DialogDescription>
            Planning pédagogique de l'action — modifiez la période, le lieu ou le collaborateur affecté à chaque séance. {dossier?.lieu_principal ? `Lieu principal : ${dossier.lieu_principal}.` : ''} Horaires {horairesLabel('journee')}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto rounded-lg border" data-testid="planning-af-table">
          {loading ? <div className="p-10 text-center text-sm text-slate-400 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div> : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-left text-slate-500">
                  <th className="px-2 py-2 font-semibold w-28">Date</th>
                  <th className="px-2 py-2 font-semibold w-36">Période</th>
                  <th className="px-2 py-2 font-semibold">Modules</th>
                  <th className="px-2 py-2 font-semibold w-48">Lieu</th>
                  <th className="px-2 py-2 font-semibold w-56">Collaborateur</th>
                  <th className="px-2 py-2 font-semibold w-40">Autres intervenants</th>
                </tr>
              </thead>
              <tbody>
                {planning.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Aucune séance planifiée.</td></tr>}
                {planning.map(p => {
                  const locked = !!p.presences_validees;
                  const isHl = p.date === highlightDate;
                  const externe = p.type_journee === 'stage' || p.type_journee === 'prestation_externe';
                  const d = parseISO(p.date);
                  const we = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <tr key={`${p.date}-${p.demi_journee}`} className={`border-t ${isHl ? 'bg-indigo-50/70' : we ? 'bg-slate-50/50' : ''} ${locked ? 'opacity-80' : ''}`} data-testid={`planning-af-row-${p.date}`}>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span className={`font-medium ${isHl ? 'text-indigo-700' : 'text-slate-800'}`}>{format(d, 'EEE dd/MM', { locale: fr })}</span>
                        {locked && <Lock className="w-3 h-3 inline ml-1 text-slate-400" title="Présences validées — séance verrouillée" />}
                      </td>
                      <td className="px-2 py-1.5">
                        <select value={p.demi_journee || 'journee'} disabled={locked} onChange={(e) => update(p.date, p.demi_journee, { demi_journee: e.target.value })} className="h-7 w-full rounded border border-slate-200 bg-white px-1 text-[11px] disabled:bg-slate-50" data-testid={`planning-af-periode-${p.date}`}>
                          {PERIODES.map(([v, l]) => <option key={v} value={v}>{l} · {horairesLabel(v)}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        {externe ? <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">{TYPE_LABEL[p.type_journee]}{p.organisme_externe ? ` — ${p.organisme_externe}` : ''}</span>
                          : (p.modules_pedagogiques || []).length ? (
                            <div className="flex flex-wrap gap-1">{p.modules_pedagogiques.map(m => <span key={m.module_id} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 truncate max-w-[220px]" title={modules[m.module_id]?.titre}>{modules[m.module_id]?.code || ''} {modules[m.module_id]?.titre || 'Module'}{m.duree_heures ? ` · ${m.duree_heures} h` : ''}</span>)}</div>
                          ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-1.5">
                        {externe ? <span className="text-slate-400">{p.organisme_externe || '—'}</span> : (
                          <select value={p.lieu || ''} disabled={locked} onChange={(e) => update(p.date, p.demi_journee, { lieu: e.target.value })} className="h-7 w-full rounded border border-slate-200 bg-white px-1 text-[11px] disabled:bg-slate-50" data-testid={`planning-af-lieu-${p.date}`}>
                            <option value="">{dossier?.lieu_principal ? `(principal) ${dossier.lieu_principal}` : '— Lieu principal —'}</option>
                            {lieuxOptions.map(l => <option key={l} value={l}>{l}</option>)}
                          </select>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        {externe ? <span className="text-slate-400">—</span> : (
                          <select value={p.formateur_id || ''} disabled={locked} onChange={(e) => update(p.date, p.demi_journee, { formateur_id: e.target.value })} className={`h-7 w-full rounded border px-1 text-[11px] disabled:bg-slate-50 ${p.formateur_id ? 'border-slate-200 bg-white' : 'border-amber-300 bg-amber-50 text-amber-800'}`} data-testid={`planning-af-formateur-${p.date}`}>
                            <option value="">— Non affecté —</option>
                            {formateurs.map(f => <option key={f.id} value={f.id}>{f.nom} {f.prenom}</option>)}
                            {p.formateur_id && !formateurs.some(f => f.id === p.formateur_id) && <option value={p.formateur_id}>{nomFormateur(p.formateur_id)}</option>}
                          </select>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        {externe ? <span className="text-slate-300">—</span> : (
                          <div className="flex flex-wrap items-center gap-1">
                            {(p.intervenants || []).map(i => (
                              <span key={i.formateur_id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200" title={i.creneau} data-testid={`planning-af-intervenant-${p.date}-${i.formateur_id}`}>
                                {nomFormateur(i.formateur_id)}
                                {!locked && <button type="button" onClick={() => update(p.date, p.demi_journee, { intervenants: p.intervenants.filter(x => x.formateur_id !== i.formateur_id) })} className="text-violet-400 hover:text-red-500" title="Retirer cet intervenant"><X className="w-3 h-3" /></button>}
                              </span>
                            ))}
                            {!locked && (
                              <select value="" onChange={(e) => { if (e.target.value) update(p.date, p.demi_journee, { intervenants: [...(p.intervenants || []), { formateur_id: e.target.value, creneau: 'journee', module_ids: [] }] }); }} className="h-6 rounded border border-dashed border-slate-300 bg-white px-1 text-[10px] text-slate-500" data-testid={`planning-af-add-intervenant-${p.date}`}>
                                <option value="">+ ajouter</option>
                                {formateurs.filter(f => f.id !== p.formateur_id && !(p.intervenants || []).some(i => i.formateur_id === f.id)).map(f => <option key={f.id} value={f.id}>{f.nom} {f.prenom}</option>)}
                              </select>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter className="gap-2">
          {dirty && <span className="text-xs text-amber-700 self-center mr-auto" data-testid="planning-af-dirty">Modifications non enregistrées</span>}
          <Button type="button" variant="outline" onClick={() => { setDirty(false); onSaved?.(); onOpenChange(false); }} disabled={saving} data-testid="planning-af-cancel">{dirty ? 'Annuler les modifications' : 'Fermer'}</Button>
          <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={save} disabled={saving || !dirty} data-testid="planning-af-save">{saving ? 'Enregistrement…' : 'Enregistrer et fermer'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
